import React, { FC } from 'react';
import { FormTab } from 'app/modules/posts/modules/detail/modules/audienceRestrictions/constants/audienceRestrictions';
import {
  AudienceRestrictionsOptions,
  AudienceRestrictions,
} from 'app/modules/posts/types/AudienceRestrictionsOptions';
import { getFormFields } from 'app/modules/posts/modules/detail/modules/audienceRestrictions/utils/audienceRestrictions';
import RestrictionSelect from 'app/modules/posts/modules/detail/modules/audienceRestrictions/components/form/RestrictionSelect';
import PageTypeUtils from 'app/utils/pageType';
import { TextLink } from '@kontentino/ui';
import Link from 'components/shared/Link';
import { profileSettingsLink } from 'app/config/settingsV2Mode';
import useSettingsV2Gate from 'app/hooks/useSettingsV2Gate';
import { TYPE_LINKEDIN } from '@kontentino/kontentino-constants/Pages';
import AudienceRestrictionsPresets from '../AudienceRestrictionsPresets';
import PostsConfig from 'app/config/posts';
import { useTranslation, Trans } from 'react-i18next';

type Props = {
  page: {
    id?: number;
    type: number;
  };
  linkedinAudience?: {
    size: number | undefined;
    loading: boolean;
  };
  onChange: (value: AudienceRestrictionsOptions) => void;
  initialValue: AudienceRestrictionsOptions;
  activeTab: FormTab;
  onPresetSelect: (audienceRestrictions: AudienceRestrictions) => void;
};

const ActiveFormTab: FC<Props> = ({
  page,
  onChange,
  initialValue,
  activeTab,
  linkedinAudience,
  onPresetSelect,
}) => {
  const { t } = useTranslation();
  const { isSettingsV2Enabled } = useSettingsV2Gate();
  function getDescription() {
    return {
      [FormTab.NewsFeedTargeting]: (
        <>
          <Trans
            i18nKey="audienceRestrictions.newsFeedTargetingDescription"
            components={{ strong: <strong /> }}
          />
        </>
      ),

      [FormTab.RestrictedAudience]: (
        <section>
          <p>
            <Trans
              i18nKey="audienceRestrictions.restrictedAudienceDescription"
              values={{
                platform: PageTypeUtils.getLabel(page.type),
              }}
            />
          </p>
          {page.type === TYPE_LINKEDIN && (
            <p className="tw-mt-1">
              <Trans
                i18nKey="audienceRestrictions.audienceMinimumRequirement"
                values={{
                  minSize:
                    PostsConfig.AUDIENCE_RESTRICTIONS.MINIMUM_LI_AUDIENCE_SIZE,
                }}
                components={{ strong: <strong /> }}
              />
            </p>
          )}
          {page.id !== undefined && (
            <TextLink>
              <Link
                to={profileSettingsLink(
                  page.id,
                  'audience-restrictions',
                  isSettingsV2Enabled,
                )}
                className="tw-mt-2 tw-block"
                target="_blank"
              >
                {t('audienceRestrictions.manageDefaultRestrictions')}
              </Link>
            </TextLink>
          )}
          {linkedinAudience && page.type === TYPE_LINKEDIN && (
            <div className="tw-mt-2  tw-inline-flex tw-items-center tw-gap-1">
              <p>{t('audienceRestrictions.audienceSize')}</p>
              {!linkedinAudience.loading &&
                linkedinAudience.size === undefined && (
                  <span className="tw-text-grayscale-120">
                    {t('audienceRestrictions.unknownAudienceSize')}
                  </span>
                )}
              {!linkedinAudience.loading &&
                linkedinAudience.size !== undefined && (
                  <span>{linkedinAudience.size}</span>
                )}
            </div>
          )}
        </section>
      ),
      [FormTab.Presets]: (
        <div>
          <p>{t('audienceRestrictions.selectFromPresets')}</p>
          {page.id !== undefined && (
            <TextLink>
              <Link
                to={profileSettingsLink(
                  page.id,
                  'audience-restrictions',
                  isSettingsV2Enabled,
                )}
                className="tw-mt-2 tw-block"
                target="_blank"
              >
                {t('audienceRestrictions.managePresets')}
              </Link>
            </TextLink>
          )}
        </div>
      ),
    }[activeTab];
  }

  return (
    <div>
      <div className="tw-mb-6 tw-mt-4 tw-text-sm">{getDescription()}</div>
      <div className="tw-flex tw-flex-col tw-gap-4 tw-pb-6">
        {activeTab === FormTab.Presets &&
        getFormFields(page.type, activeTab).length === 0 ? (
          <AudienceRestrictionsPresets
            onPresetSelect={onPresetSelect}
            pageType={page.type}
          />
        ) : (
          getFormFields(page.type, activeTab).map((field) => (
            <RestrictionSelect
              key={field.type}
              label={field.label}
              type={field.type}
              placeholder={field.placeholder}
              isSingle={field.isSingle}
              onChange={onChange}
              initialValue={initialValue[field.type]}
              pageType={page.type}
            />
          ))
        )}
      </div>
    </div>
  );
};

export default ActiveFormTab;
